import { makeStyles, tokens } from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import {
  FetchXmlBuilder,
  MainGrid,
  PagingMode,
  usePowerPortalsProFetchXmlMetadata,
} from '@powerportalspro/react-fluent';
import { useState } from 'react';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
    // A flex item's `min-width` is its content unless told otherwise, and a results
    // grid with a dozen columns is wide content. Without this it pushes the docs
    // page wider instead of scrolling inside the grid, which is where the grid is
    // built to put the scrollbar.
    minWidth: 0,
  },
});

// Reuses the Blazor page's localization keys so the docs page and the sample say
// the same thing in every language.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors' +
  '.FetchXmlQueryBuilder.FetchXmlQueryBuilderDemoPage';

/**
 * The one view the Run panel's grid ever shows.
 *
 * Fixed rather than generated: the grid keys its view state by this, and a new id
 * per render would read as a different view every time the query changed.
 */
const BUILDER_VIEW_ID = 'c4b9a1e2-7d3f-4a6b-9c15-2f8e6d41b703';

/**
 * The tables this demo has data for.
 *
 * `allowedTables` narrows the table picker to a set the surface actually supports
 * — a report builder over the handful of tables a portal exposes has no business
 * offering the whole environment. Omit it and every table is offered.
 */
const DEMO_TABLES = ['account', 'contact', 'opportunity'] as const;

/**
 * The builder, wired the way a portal page wires it.
 *
 * Everything the component needs is the metadata provider — the hook reads the
 * framework's cached metadata endpoints, so the table picker lists this
 * environment's real tables and the operator dropdown narrows to each column's
 * real type. `renderResults` is what adds the Run button; the builder decides when
 * it may be pressed, and this decides what a result looks like.
 */
export function FetchXmlBuilderMainSample() {
  const styles = useStyles();
  const t = useT();
  const metadata = usePowerPortalsProFetchXmlMetadata();
  const [fetchXml, setFetchXml] = useState(
    '<fetch>\n  <entity name="contact">\n    <attribute name="fullname" />\n' +
      '    <attribute name="emailaddress1" />\n    <order attribute="fullname" />\n' +
      '  </entity>\n</fetch>',
  );

  return (
    <div className={styles.root}>
      <FetchXmlBuilder
        value={fetchXml}
        onChange={setFetchXml}
        metadata={metadata}
        allowedTables={DEMO_TABLES}
        renderResults={(runnable) => (
          <MainGrid
            // Remounts when the query text changes so a re-run starts from a clean
            // grid rather than reconciling against the previous view.
            key={runnable.fetchXml}
            tableName={runnable.tableName}
            customViewDefinitions={[
              {
                id: BUILDER_VIEW_ID,
                displayName: t(`${STRINGS_BASE}.live-view-name`),
                tableName: runnable.tableName,
                fetchXml: runnable.fetchXml,
                columns: runnable.columns.map((columnName) => ({ columnName, width: 200 })),
              },
            ]}
            // Pinned rather than defaulted: the table's saved views are not what is
            // being built here, and pinning also skips listing them.
            viewId={BUILDER_VIEW_ID}
            title={t(`${STRINGS_BASE}.live-view-name`)}
            // Infinite scroll needs something to scroll inside; uncapped, the grid
            // grows to fit every row and fetches the next page immediately.
            pagingMode={PagingMode.Virtualize}
            selectionMode="none"
            pageSize={25}
            minHeight="240px"
            maxHeight="400px"
          />
        )}
      />
    </div>
  );
}
